// src/components/HomePageComponents/HomeSectionFour.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Container, Paper, Stack, TextField, Button,
    Typography, Alert, MenuItem, FormControlLabel, Checkbox, LinearProgress
} from '@mui/material';
import { sendContactForm } from '../../utils/utils';
import { loadRecaptcha, renderRecaptcha, getRecaptchaToken, resetRecaptcha, isRecaptchaConfigured } from '../../utils/recaptcha';
import { FONT_WEIGHT } from '../../theme/fonts';

// Using CSS variables from index.css for consistency with mobile app
const brand = { 
  primary: 'var(--primary-color, #047857)',  // emerald-700
  primaryHover: 'var(--primary-color-hover, #065f46)',  // emerald-800
  accent: 'var(--secondary-color, #0e918c)',  // teal
};

export default function HomeSectionFour() {
    const [values, setValues] = useState({
        name: '',
        email: '',
        category: 'General feedback',
        feedback: '',
        allowFollowUp: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');
    const [, setRecaptchaReady] = useState(false);
    const [recaptchaContainerReady, setRecaptchaContainerReady] = useState(false);
    const recaptchaContainerRef = useRef(null);

    // Render reCAPTCHA when the container is in the DOM and key is configured
    useEffect(() => {
        if (!recaptchaContainerReady || !isRecaptchaConfigured()) return;

        let cancelled = false;
        const initRecaptcha = async () => {
            try {
                await loadRecaptcha();
                if (cancelled) return;
                // Ensure container is in document and layout is done
                const id = 'recaptcha-container-feedback';
                const delay = window.requestAnimationFrame ? () => new Promise(r => { requestAnimationFrame(() => requestAnimationFrame(r)); }) : () => new Promise(r => setTimeout(r, 50));
                await delay();
                if (cancelled || !document.getElementById(id)) return;
                const widgetId = await renderRecaptcha(id);
                if (widgetId !== null) setRecaptchaReady(true);
            } catch (err) {
                if (!cancelled) console.error('Failed to initialize reCAPTCHA:', err);
            }
        };
        initRecaptcha();
        return () => { cancelled = true; };
    }, [recaptchaContainerReady]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setValues((v) => ({ ...v, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setDone(false);

        if (!values.feedback.trim()) {
            setError('Please share your feedback.');
            return;
        }

        // Check reCAPTCHA if configured
        if (isRecaptchaConfigured()) {
            const recaptchaToken = getRecaptchaToken();
            if (!recaptchaToken) {
                setError('Please complete the "I\'m not a robot" checkbox.');
                return;
            }
        }

        setSubmitting(true);
        try {
            await sendContactForm({
                name: values.name || undefined,
                email: values.email || undefined,
                category: values.category,
                message: values.feedback,
                allowFollowUp: values.allowFollowUp,
                source: 'home_feedback_section',
            });
            setDone(true);
            setValues({
                name: '',
                email: '',
                category: 'General feedback',
                feedback: '',
                allowFollowUp: true,
            });
            // Reset reCAPTCHA for next submission
            resetRecaptcha();
        } catch (err) {
            setError(err?.message || 'Something went wrong. Please try again.');
            // Reset reCAPTCHA on error so user can try again
            resetRecaptcha();
        } finally {
            setSubmitting(false);
        }
    };

    const remaining = Math.max(0, 1000 - values.feedback.length);

    return (
        <Box sx={{ py: { xs: 5, md: 6 }, bgcolor: '#f8f9fa' }}>
            <Container maxWidth="sm">
                <Paper elevation={2} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
                    <Stack spacing={1.5} component="form" onSubmit={handleSubmit}>
                        <Typography variant="h6" sx={{ fontWeight: FONT_WEIGHT.bold }}>
                            We’d love to hear from you
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                            CivixThread is built for constituents like you. Your feedback helps shape where it goes next.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Tell us what’s working, what could improve, and what you’d love to see added.
                        </Typography>

                        {submitting && <LinearProgress />}

                        {!!error && <Alert severity="error" sx={{ my: 0.5 }}>{error}</Alert>}
                        {done && <Alert severity="success" sx={{ my: 0.5 }}>We really appreciate your input! Together, we’re building a platform that reflects what constituents actually need.</Alert>}

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <TextField
                                label="Name"
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                fullWidth
                                autoComplete="name"
                                size="small"
                                variant="outlined"
                            />
                            <TextField
                                label="Email"
                                name="email"
                                type="email"
                                value={values.email}
                                onChange={handleChange}
                                fullWidth
                                autoComplete="email"
                                size="small"
                                variant="outlined"
                            />
                        </Stack>

                        <TextField
                            select
                            label="Topic"
                            name="category"
                            value={values.category}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            variant="outlined"
                        >
                            {[
                                'General feedback',
                                'Something I like',
                                'Something I don’t like',
                                'Feature request',
                                'Bug / issue',
                            ].map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Your feedback"
                            name="feedback"
                            value={values.feedback}
                            onChange={handleChange}
                            multiline
                            minRows={3}
                            fullWidth
                            size="small"
                            variant="outlined"
                            inputProps={{ maxLength: 1000 }}
                            helperText={`${remaining} characters left`}
                            required
                        />

                        <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: 14, color: 'text.secondary' } }}
                            control={
                                <Checkbox
                                    checked={values.allowFollowUp}
                                    onChange={handleChange}
                                    name="allowFollowUp"
                                    size="small"
                                />
                            }
                            label="It's okay to contact me about my feedback"
                        />

                        {/* reCAPTCHA widget - container ref ensures we render after DOM is ready */}
                        <Box
                            ref={(el) => {
                                recaptchaContainerRef.current = el;
                                if (el && !recaptchaContainerReady) setRecaptchaContainerReady(true);
                            }}
                            sx={{ my: 1 }}
                        >
                            <div id="recaptcha-container-feedback"></div>
                        </Box>

                        <Stack direction="row" spacing={1.25}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={submitting}
                                sx={{
                                    bgcolor: brand.primary,
                                    textTransform: 'none',
                                    fontWeight: FONT_WEIGHT.semibold,
                                    borderRadius: 2,
                                    px: 2.25,
                                    py: 0.5,
                                    '&:hover': { bgcolor: brand.primaryHover },
                                }}
                            >
                                Submit feedback
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
